import classNames from "classnames/dedupe";
import React, { forwardRef, useContext } from "react";
import PageSpinner from "../../../FeedbackIndicators/PageSpinner/PageSpinner";
import LinkContext from "../../Link/LinkContext";
import { TButtonProps, TButtonType } from "./Button.interface";
import { darkButtons } from "./darkButtons";

const ButtonVariantClassMapping: Record<TButtonType, string> = {
  primary: "btn btn-primary",
  secondary: "btn btn-secondary",
  conversion: "btn btn-conversion",
  icon: "btn btn-icon-only",
  iconMedium: "btn btn-icon-only-medium",
  iconSmall: "btn btn-icon-only-small",
  iconDisabled: "btn btn-icon-only disabled",
  largeIcon: "btn btn-icon-only btn-lg",
  iconHeart: "btn btn-icon-custom btn-icon-heart-blue",
  iconHeartActive: "btn btn-icon-custom btn-icon-heart-blue active",
  payment: "btn btn-payment",
  tag: "btn-tag",
  cancel: "btn btn-cancel",
  submit: "btn btn-submit",
  modalClose: "btn btn--modal-close",
  hoverSlide: "shop-look__btn",
  circleMarker: "shop-look__marker",
} as const;

export const Button = forwardRef<HTMLButtonElement, TButtonProps>(
  (
    {
      variant,
      disabled = false,
      type = "button",
      as: Tag,
      size,
      style,
      classes,
      loading,
      onClick,
      children,
      ...other
    },
    ref,
  ) => {
    const rootClasses = classNames(
      ButtonVariantClassMapping[variant],
      { "btn-sm": size === "small" },
      { "btn-lg": size === "large" },
      classes?.root,
    );

    const { wrapper: LinkCustomContextWrapper } = useContext(LinkContext);
    const RenderComponent = Tag || LinkCustomContextWrapper || "button";

    return (
      <RenderComponent
        className={rootClasses}
        disabled={disabled}
        style={style}
        // eslint-disable-next-line react/button-has-type
        type={type}
        ref={ref}
        data-testid="button"
        onClick={onClick}
        {...other}
      >
        {loading ? (
          <PageSpinner
            classes={{ root: classes?.spinner }}
            type="button"
            theme={darkButtons.includes(variant) ? "light" : "dark"}
          />
        ) : (
          children
        )}
      </RenderComponent>
    );
  },
);
